/**
 * Event types for the Event Aggregator system
 *
 * Events group related measurements into coherent incidents spanning
 * multiple cameras, sensors, and time windows.
 */

import type { CopiedFrom } from "./common";
import type { BaseDocument, Ref } from "./data";
import type { Installation } from "./installation";
import type { GeoJSONPoint } from "./map";
import type { Measurement } from "./measurement";
import type { SensorStatus, SensorType } from "./sensor";
import type { Zone } from "./zone";

export enum EventSeverity {
	Critical = "critical",
	High = "high",
	Low = "low",
	Moderate = "moderate",
}

/**
 * Status of an Event in the aggregation system
 */
export enum EventStatus {
	/** Event is open and visible on the Alerts Dashboard */
	Open = "open",
	/** Event has been resolved and moved to All Activity */
	Resolved = "resolved",
}

export enum EventType {
	Environment = "environment",
	Health = "health",
	Operations = "operations",
	Other = "other",
	Safety = "safety",
	Security = "security",
}

export enum ReopenReason {
	IssueReoccurred = "issue_reoccurred",
	NewEvidenceFound = "new_evidence_found",
	Other = "other",
	ResolvedInError = "resolved_in_error",
}

export enum ResolutionReason {
	ActionTaken = "action_taken",
	DuplicateAlert = "duplicate_alert",
	FalseAlarm = "false_alarm",
	NoIssueFound = "no_issue_found",
	Other = "other",
	ReferencedForLater = "referenced_for_later",
	TestDrill = "test_drill",
}

/**
 * Parameters for creating a new Event
 */
export interface CreateEventParams {
	/** Whether the camera was damaged by vandalism */
	cameraDamaged?: boolean;
	/** Whether the camera was obstructed by vandalism */
	cameraObstructed?: boolean;
	/** Geographic centroid of the event */
	centroid: GeoJSONPoint;
	/** Whether a collision was confirmed by enhanced analysis */
	collisionConfirmed?: boolean;
	/** Provenance, set only by the cross-tenant copy tool */
	copiedFrom?: CopiedFrom;
	/** Cause of camera/property damage (thrown_rock, thrown_object, physical_grab, etc.) */
	damageCause?: string;
	/** AI-generated description */
	description: string;
	/** Timestamp when the event ended */
	endTimestamp: Date;
	/** Whether a fire/electrical hazard was confirmed by enhanced analysis */
	fireConfirmed?: boolean;
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/** Event-time of the measurement that opened this event; never changes. */
	firstMeasurementAt?: Date;
	/** Installation ID this event belongs to */
	installation: string;
	/** Initial measurement IDs for the event */
	measurements: string[];
	/** S3 keys for video files associated with this event (denormalized from measurements) */
	mediaKeys?: string[];
	/**
	 * Whether every measurement on this event is a third party's assertion,
	 * with no analysis of ours yet — the state the "Preliminary Data" caption
	 * describes.
	 *
	 * Owned by the aggregator rather than derived in the client, because the
	 * client cannot answer it reliably: `measurements` is populated only when a
	 * request asks for it, so an unpopulated event is indistinguishable from a
	 * preliminary one.
	 *
	 * MONOTONIC. It starts true when a provider opens the event and is cleared
	 * the moment any measurement of ours joins; nothing sets it back. An event
	 * we have analysed is not awaiting analysis, however many further provider
	 * assertions arrive afterwards.
	 */
	preliminary?: boolean;
	/** Sensor ids contributing to this event (denormalized from measurements) */
	sensors?: string[];
	/** Severity score (1-10) */
	severity: number;
	/** Timestamp when the event started */
	startTimestamp: Date;
	/** Initial status (defaults to 'active') */
	status?: EventStatus;
	/** Whether a threat was confirmed by enhanced analysis */
	threatConfirmed?: boolean;
	/** S3 keys for thumbnail images associated with this event (denormalized from measurements) */
	thumbnailKeys?: string[];
	/** AI-generated title */
	title: string;
	/** Event type classifications */
	types?: EventType[];
	/** Whether vandalism was confirmed by enhanced analysis */
	vandalismConfirmed?: boolean;
	/**
	 * Watch ids this event matched at severity ≥ 5 (`$addToSet` of BOLO
	 * `pipelineLabel`s during aggregation). Drives the "events for a watch"
	 * filter and the watch alert counter.
	 *
	 * Each entry is a BOLO instance's `pipelineLabel`. For Watch-created BOLOs
	 * that label is the `Watch._id` (the label-is-watch-id contract — see
	 * `PipelineInstance`). NOTE: entries are not a guaranteed foreign key to the
	 * Watch collection — a raw/manually-created BOLO instance contributes its
	 * label here with no Watch record behind it, so resolve titles defensively.
	 */
	watches?: string[];
	/** Whether a weapon was detected by enhanced analysis */
	weaponDetected?: boolean;
	/** Type of weapon detected (e.g., "firearm", "knife") */
	weaponType?: string;
	/** Zone IDs that this event spans (denormalized from measurements) */
	zones?: string[];
}

/**
 * Event represents a real-world incident spanning multiple measurements
 * from various cameras, sensors, and time windows
 */
export interface Event extends BaseDocument {
	/** Whether the camera was damaged by vandalism */
	cameraDamaged?: boolean;
	/** Whether the camera was obstructed by vandalism */
	cameraObstructed?: boolean;
	/** Geographic centroid of the event, derived from sensor locations */
	centroid: GeoJSONPoint;
	/** Whether a collision was confirmed by enhanced analysis */
	collisionConfirmed?: boolean;
	/** Provenance, set only on events created by the cross-tenant copy tool */
	copiedFrom?: CopiedFrom;
	/** Cause of camera/property damage (thrown_rock, thrown_object, physical_grab, etc.) */
	damageCause?: string;
	/** AI-generated description synthesizing all measurements in the event */
	description: string;
	/** Timestamp when the last measurement in the event ended */
	endTimestamp: Date;
	/** Whether a fire/electrical hazard was confirmed by enhanced analysis */
	fireConfirmed?: boolean;
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/**
	 * Event-time of the measurement that opened this event.
	 *
	 * Written once at creation and never updated, which is the whole point:
	 * `startTimestamp` is `$min`-ed and `endTimestamp` is `$max`-ed, so both
	 * migrate outward as measurements join. Sensor pairing bounds its search on
	 * this instead, so an event's reach cannot grow by absorbing measurements —
	 * a measurement that lands earlier than the event start would otherwise pull
	 * the start back and re-open the window another window-width into the past,
	 * one step per out-of-order arrival.
	 *
	 * Absent on events created before the field existed; those never pair.
	 */
	firstMeasurementAt?: Date;
	/** Reference to the Installation this event belongs to */
	installation: Ref<Installation>;
	/** Per-field attribution of the most recent human edit to each field. */
	lastEditedBy?: EventFieldEditors;
	/** Array of measurement references that belong to this event */
	measurements: Ref<Measurement>[];
	/** S3 keys for video files associated with this event (denormalized from measurements for performance) */
	mediaKeys: string[];
	/**
	 * Whether every measurement on this event is a third party's assertion,
	 * with no analysis of ours yet — the state the "Preliminary Data" caption
	 * describes.
	 *
	 * Owned by the aggregator rather than derived in the client, because the
	 * client cannot answer it reliably: `measurements` is populated only when a
	 * request asks for it, so an unpopulated event is indistinguishable from a
	 * preliminary one.
	 *
	 * MONOTONIC. It starts true when a provider opens the event and is cleared
	 * the moment any measurement of ours joins; nothing sets it back. An event
	 * we have analysed is not awaiting analysis, however many further provider
	 * assertions arrive afterwards.
	 */
	preliminary?: boolean;
	/**
	 * The title, description, and types the event carried before a
	 * plausibility verification down-ranked it and the summary was
	 * regenerated. Written once, on the first down-rank, and never shown to
	 * customers: it is the record of what the system first claimed, kept for
	 * staff review and calibration.
	 */
	preVerificationSummary?: PreVerificationSummary;
	/**
	 * Sensor ids contributing to this event, denormalized from its measurements
	 * via `$addToSet`. Exactly the set you would get by walking the event's
	 * measurements to their sensors — stored so the pairing step can ask "is
	 * there an open event on this sensor?" in one indexed lookup instead of an
	 * aggregation through the measurements collection.
	 *
	 * Ids as stored; `EventSensor` objects once a read path resolves them. The
	 * element type admits both because the two must stay interchangeable: the
	 * dashboard holds resolved events in caches typed as plain `Event`, so
	 * narrowing this to ids alone makes `EventWithSensors` unassignable to
	 * `Event`, and narrowing it to objects alone breaks every write.
	 *
	 * Consumers that render sensors resolve them with `resolvedSensors`, which
	 * is honest about the ids being possible rather than asserting they are not.
	 *
	 * Optional because events predating the field carry none; the read path
	 * derives them from measurements for those.
	 */
	sensors?: (EventSensor | string)[];
	/** Severity score (1-10), derived from highest measurement severity */
	severity: number;
	/** Timestamp when the first measurement in the event started */
	startTimestamp: Date;
	/** Current status of the event */
	status: EventStatus;
	/** Whether a threat was confirmed by enhanced analysis */
	threatConfirmed?: boolean;
	/** S3 keys for thumbnail images associated with this event (denormalized from measurements for performance) */
	thumbnailKeys: string[];
	/** AI-generated user-facing title summarizing the incident */
	title: string;
	/** Event type classifications */
	types?: EventType[];
	/** Whether vandalism was confirmed by enhanced analysis */
	vandalismConfirmed?: boolean;
	/**
	 * Watch ids this event matched at severity ≥ 5. Denormalized via
	 * `$addToSet` of each qualifying BOLO measurement's `pipelineLabel` so the
	 * dashboard can badge matched subjects and the API can filter
	 * `GET /events?watches[]=…` without touching the measurements collection.
	 *
	 * Each entry is a BOLO `pipelineLabel`, which for Watch-created BOLOs is the
	 * `Watch._id` (the label-is-watch-id contract — see `PipelineInstance`).
	 * NOTE: not a guaranteed foreign key — a raw/manually-created BOLO instance
	 * contributes its label here with no Watch record behind it, so resolve
	 * titles defensively (fall back to the raw id).
	 */
	watches?: string[];
	/** Whether a weapon was detected by enhanced analysis */
	weaponDetected?: boolean;
	/** Type of weapon detected (e.g., "firearm", "knife") */
	weaponType?: string;
	/** Array of zone references that this event spans (denormalized from measurements) */
	zones: Ref<Zone>[];
}

/** Who last edited a single event field, and when. */
export interface EventFieldEditor {
	/** When the edit happened. */
	at: Date;
	/**
	 * Display name of the editor, resolved from Kinde at write time and cached
	 * here so reads need no round trip. Degrades to "Unknown" when Kinde is
	 * unavailable — repair it from `userId` rather than trusting it blindly.
	 */
	name: string;
	/**
	 * Kinde `sub` of the editor — the stable identity. `name` is a cache that can
	 * be stale (after a rename) or degraded (during a Kinde outage); this is what
	 * makes either repairable, and it mirrors `EventHistory.actor`.
	 */
	userId: string;
}

/** Per-field edit attribution for the human-editable fields of an event. */
export interface EventFieldEditors {
	description?: EventFieldEditor;
	severity?: EventFieldEditor;
	title?: EventFieldEditor;
	types?: EventFieldEditor;
}

/**
 * Event search fields type where centroid is a "lng,lat" string
 * instead of a GeoJSONPoint object (for query parameter parsing)
 */
export type EventSearchFields = Omit<Event, "centroid"> & { centroid?: string };

/**
 * DTO shape consumed by `EventService.search`. Mirrors the post-validation
 * search fields exposed by the `GET /events` endpoint, minus pagination and
 * include/populate options (which the handler strips off before calling the
 * service). All fields are optional — an empty object means "no filter".
 */
export interface EventSearchInput {
	centroid?: string;
	endTimestamp?: string;
	installationIds?: string[];
	locationIds?: string[];
	sensorIds?: string[];
	severity?: number;
	severityMin?: number;
	startTimestamp?: string;
	status?: EventStatus;
	title?: string;
	types?: string[];
	/**
	 * Match events whose `watches` array contains any of these watch ids
	 * (`watches[]` query param). Drives the "events for a watch" lookup.
	 */
	watches?: string[];
	zones?: string[];
}

/**
 * Computed sensor reference on event search results
 * Derived from event → measurements → measurement.sensor
 */
export interface EventSensor {
	/** Sensor geolocation, used to plot the air-quality map marker */
	geolocation?: GeoJSONPoint;
	id: string;
	/** Timestamp of the last input received, shown in the marker popup */
	lastInputReceived?: Date | null;
	name: string;
	/** Current sensor status, shown in the marker popup */
	status?: SensorStatus;
	/** Sensor type, shown in the marker popup */
	type?: SensorType;
}

/**
 * Event summary for dashboard status box
 * Contains counts for the current and previous 24-hour periods
 */
export interface EventStats {
	/** Count of events with severity >= threshold in current 24-hour period */
	count: number;
	/** Percent change from previous period to current period */
	percentChange: number;
	/** Count of events with severity >= threshold in previous 24-hour period */
	previousCount: number;
}

/**
 * Event with its sensors resolved to the fields the UI renders.
 */
export interface EventWithSensors extends Event {
	sensors: EventSensor[];
}

export interface GeneratedEventSummary {
	/** AI-generated description synthesizing all measurement information */
	description: string;
	/** Concise user-facing title (< 100 chars) */
	title: string;
	/** Classified event types (Safety, Security, Operations) */
	types: EventType[];
}

/**
 * AI-generated summary for an event, produced by the LLM summary service
 */
/** What an event said before verification rewrote its summary. */
export interface PreVerificationSummary {
	at: Date;
	description: string;
	title: string;
	types?: EventType[];
}

/**
 * Parameters for updating an existing Event
 * All fields are optional to support partial updates
 */
export interface UpdateEventParams {
	/** Updated centroid if sensors change */
	centroid?: GeoJSONPoint;
	/** Whether a collision was confirmed by enhanced analysis */
	collisionConfirmed?: boolean;
	/** Updated AI-generated description */
	description?: string;
	/** Updated end timestamp as new measurements arrive */
	endTimestamp?: Date;
	/** Whether a fire/electrical hazard was confirmed by enhanced analysis */
	fireConfirmed?: boolean;
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/** Per-field attribution of the most recent human edit to each field. */
	lastEditedBy?: EventFieldEditors;
	/** Additional measurement IDs to add */
	measurements?: string[];
	/** Additional media keys to add */
	mediaKeys?: string[];
	/** Updated severity (recalculated from measurement severities) */
	severity?: number;
	/** Updated status (e.g., closing the event) */
	status?: EventStatus;
	/** Whether a threat was confirmed by enhanced analysis */
	threatConfirmed?: boolean;
	/** Additional thumbnail keys to add */
	thumbnailKeys?: string[];
	/** Updated AI-generated title */
	title?: string;
	/** Updated event type classifications */
	types?: EventType[];
	/** Whether a weapon was detected by enhanced analysis */
	weaponDetected?: boolean;
	/** Type of weapon detected (e.g., "firearm", "knife") */
	weaponType?: string;
	/** Additional zone IDs to add */
	zones?: string[];
}

/**
 * The sensors on an event that have been resolved to objects.
 *
 * `Event.sensors` may still hold ids — that is what the document stores, and a
 * cache can hold either — so anything rendering sensor detail filters first
 * rather than asserting. Returns empty for an unresolved event, which renders
 * as "no sensors" instead of throwing on a missing `name`.
 */
export function resolvedSensors(
	sensors: (EventSensor | string)[] | undefined,
): EventSensor[] {
	return (sensors ?? []).filter(
		(sensor): sensor is EventSensor => typeof sensor !== "string",
	);
}
